<template>
  <div v-if="options.length" class="bts">
    <p v-if="showLabel" class="bts__label">
      {{ $t("Select appointment type") }}
      <span v-if="options.length > 1" class="bts__count">{{
        options.length
      }}</span>
    </p>
    <div
      class="bts__list"
      :class="{ 'bts__list--scroll': options.length > 3 }"
      role="radiogroup"
    >
      <div
        v-for="option in options"
        :key="option.key"
        role="radio"
        tabindex="0"
        :aria-checked="selectedKey === option.key"
        class="bts__item"
        :class="{ 'bts__item--selected': selectedKey === option.key }"
        @click="$emit('select', option.key)"
        @keydown.enter.prevent="$emit('select', option.key)"
        @keydown.space.prevent="$emit('select', option.key)"
      >
        <span class="bts__main">
          <span class="bts__top">
            <span class="bts__title">{{ option.label }}</span>
            <span v-if="option.price" class="bts__price">{{
              option.priceIsFrom
                ? $t("From {price}", { price: option.price })
                : option.price
            }}</span>
          </span>
          <span v-if="option.depositDisplay" class="bts__deposit">
            {{ $t("Deposit today: {amount}", { amount: option.depositDisplay }) }}
            · {{ $t("The rest is paid at the center") }}
          </span>
          <transition name="bts-media">
            <span
              v-if="isDescOpen(option) && hasImage(option)"
              class="bts__media"
            >
              <img
                :src="option.image"
                :alt="option.label"
                loading="lazy"
                decoding="async"
                @error="onImgError(option)"
              />
            </span>
          </transition>
          <span
            v-if="option.description"
            class="bts__desc"
            :class="{ 'bts__desc--clamp': !isDescOpen(option) }"
            >{{ option.description }}</span
          >
          <button
            v-if="
              (option.description && option.description.length > 90) ||
              hasImage(option)
            "
            type="button"
            class="bts__more"
            :class="{ 'bts__more--open': isDescOpen(option) }"
            @click.stop="toggleDesc(option)"
          >
            <span class="bts__more-label">{{
              isDescOpen(option) ? $t("Show less") : $t("Show more")
            }}</span>
            <span class="bts__more-caret" aria-hidden="true"></span>
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BookingTypeSelector",
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    selectedKey: {
      type: String,
      default: null,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["select"],
  data() {
    return {
      descOpen: {},
      imgErrors: {},
    };
  },
  methods: {
    isDescOpen(option) {
      return !!this.descOpen[option.key];
    },
    toggleDesc(option) {
      const willOpen = !this.descOpen[option.key];
      this.descOpen = {
        ...this.descOpen,
        [option.key]: willOpen,
      };
      if (willOpen && this.selectedKey !== option.key) {
        this.$emit("select", option.key);
      }
    },
    hasImage(option) {
      return !!option.image && !this.imgErrors[option.key];
    },
    onImgError(option) {
      this.imgErrors = { ...this.imgErrors, [option.key]: true };
    },
  },
};
</script>

<style scoped>
.bts__label {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1c1b1a;
  margin-bottom: 0.4rem;
}

.bts__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.9rem;
  height: 1.9rem;
  margin-left: 0.4rem;
  padding: 0 0.5rem;
  border-radius: 999px;
  background: rgba(91, 111, 238, 0.1);
  color: #5b6fee;
  font-size: 1.05rem;
  font-weight: 700;
  vertical-align: middle;
}

.bts__list {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.bts__list--scroll {
  max-height: 23rem;
  overflow-y: auto;
  padding-right: 0.5rem;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(91, 111, 238, 0.4) rgba(0, 0, 0, 0.05);
  -webkit-mask-image: linear-gradient(
    to bottom,
    #000 calc(100% - 1.6rem),
    transparent
  );
  mask-image: linear-gradient(to bottom, #000 calc(100% - 1.6rem), transparent);
}

.bts__list--scroll::-webkit-scrollbar {
  width: 0.7rem;
}

.bts__list--scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 999px;
}

.bts__list--scroll::-webkit-scrollbar-thumb {
  background: rgba(91, 111, 238, 0.4);
  border-radius: 999px;
}

.bts__item {
  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 1.05rem 1.2rem;
  border-radius: 1.2rem;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06),
    0 2px 6px -2px rgba(15, 23, 42, 0.05);
  transition: box-shadow 0.22s ease, transform 0.22s ease;
}

.bts__item:not(.bts__item--selected):hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px -10px rgba(15, 23, 42, 0.28);
}

.bts__item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(91, 111, 238, 0.2);
}

.bts__item--selected {
  box-shadow: 0 10px 24px -10px rgba(91, 111, 238, 0.55);
}

.bts__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.bts__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.bts__title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  transition: color 0.18s ease;
}

.bts__item:not(.bts__item--selected):hover .bts__title {
  color: #3346d3;
}

.bts__item--selected .bts__title {
  color: #3346d3;
  font-weight: 700;
}

.bts__deposit {
  display: block;
  margin-top: 0.35rem;
  font-size: 1.15rem;
  color: rgba(0, 0, 0, 0.55);
}

.bts__item--selected .bts__deposit {
  color: rgba(0, 0, 0, 0.7);
}

.bts__price {
  flex-shrink: 0;
  align-self: center;
  padding: 0.45rem 1rem;
  border: 1.5px solid rgba(91, 111, 238, 0.35);
  border-radius: 999px;
  font-size: 1.3rem;
  font-weight: 700;
  color: #5b6fee;
  letter-spacing: -0.01em;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.bts__item:not(.bts__item--selected):hover .bts__price {
  border-color: #5b6fee;
  background: rgba(91, 111, 238, 0.08);
}

.bts__item--selected .bts__price {
  color: #ffffff;
  background: #5b6fee;
  border-color: #5b6fee;
  box-shadow: 0 6px 14px -6px rgba(91, 111, 238, 0.6);
}

.bts__media {
  display: block;
  width: 100%;
  height: 15rem;
  margin: 0.4rem 0 0.2rem;
  border-radius: 1rem;
  overflow: hidden;
  background: #f3f4f6;
}

.bts__media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bts-media-enter-active,
.bts-media-leave-active {
  overflow: hidden;
  transition: max-height 0.34s ease, opacity 0.28s ease, margin 0.34s ease;
}

.bts-media-enter-from,
.bts-media-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.bts-media-enter-to,
.bts-media-leave-from {
  max-height: 15.6rem;
  opacity: 1;
}

.bts__desc {
  display: block;
  font-size: 1.15rem;
  line-height: 1.45;
  color: #6b7280;
  max-height: 40rem;
  overflow: hidden;
  transition: max-height 0.34s ease;
}

.bts__desc--clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  max-height: 3.4rem;
}

.bts__more {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  align-self: flex-start;
  margin-top: 0.2rem;
  padding: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #5b6fee;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.bts__more-label {
  transition: text-decoration-color 0.18s ease;
}

.bts__more:hover .bts__more-label {
  text-decoration: underline;
}

.bts__more:hover {
  color: #3346d3;
}

.bts__more-caret {
  display: inline-block;
  width: 0.55rem;
  height: 0.55rem;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.28s ease;
}

.bts__more--open .bts__more-caret {
  transform: rotate(-135deg);
}
</style>
